'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface InAppNotifica {
  id: string;
  allarme_id: string | null;
  oggetto: string;
  corpo: string;
  stato: string;
  priorita: string;
  created_at: string;
  letta_at: string | null;
  allarmi?: { titolo: string; priorita: string } | null;
}

const PRIORITA_COLORS: Record<string, string> = {
  critico: 'border-l-red-500 bg-red-500/5',
  alto: 'border-l-orange-500 bg-orange-500/5',
  medio: 'border-l-yellow-500 bg-yellow-500/5',
  basso: 'border-l-green-500 bg-green-500/5',
  info: 'border-l-sky-500 bg-sky-500/5',
};

const PRIORITA_DOT: Record<string, string> = {
  critico: 'bg-red-500',
  alto: 'bg-orange-500',
  medio: 'bg-yellow-500',
  basso: 'bg-green-500',
  info: 'bg-sky-500',
};

export default function AlarmNotificationBell() {
  const { user } = useAuth();
  const [notifiche, setNotifiche] = useState<InAppNotifica[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchNotifiche = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from('notifiche')
        .select('*, allarmi(titolo, priorita)')
        .eq('canale', 'in_app')
        .eq('user_id', user.id)
        .neq('stato', 'letta')
        .order('created_at', { ascending: false })
        .limit(20);
      setNotifiche((data as InAppNotifica[]) || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifiche();
    // Subscribe to realtime new notifications
    const channel = supabase
      .channel('in_app_notifiche')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifiche',
        filter: 'canale=eq.in_app',
      }, () => {
        fetchNotifiche();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchNotifiche]);

  const markRead = async (id: string) => {
    await supabase
      .from('notifiche')
      .update({ stato: 'letta', letta_at: new Date().toISOString() })
      .eq('id', id);
    setNotifiche(prev => prev.filter(n => n.id !== id));
  };

  const markAllRead = async () => {
    const ids = notifiche.map(n => n.id);
    if (ids.length === 0) return;
    await supabase
      .from('notifiche')
      .update({ stato: 'letta', letta_at: new Date().toISOString() })
      .in('id', ids);
    setNotifiche([]);
    setOpen(false);
  };

  const unreadCount = notifiche.length;
  const criticalCount = notifiche.filter(n => n.allarmi?.priorita === 'critico').length;

  const timeAgo = (d: string) => {
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'ora';
    if (mins < 60) return `${mins}m fa`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h fa`;
    return `${Math.floor(hrs / 24)}g fa`;
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg text-muted-color hover:text-white hover:bg-surface-elevated transition-colors"
        aria-label="Notifiche"
      >
        <Icon name="BellIcon" size={20} />
        {unreadCount > 0 && (
          <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full text-xs font-bold flex items-center justify-center px-1 ${criticalCount > 0 ? 'bg-red-500 animate-pulse' : 'bg-sky-500'} text-white`}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          {/* Panel */}
          <div className="absolute right-0 top-full mt-2 w-80 bg-surface border border-border rounded-2xl shadow-2xl z-50 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Icon name="BellIcon" size={16} className="text-sky-400" />
                <span className="text-sm font-bold text-white">Notifiche</span>
                {unreadCount > 0 && (
                  <span className="text-xs bg-sky-500/20 text-sky-400 px-1.5 py-0.5 rounded-full font-semibold">{unreadCount}</span>
                )}
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-sky-400 hover:text-sky-300 font-semibold transition-colors"
                >
                  Segna tutte lette
                </button>
              )}
            </div>

            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-5 h-5 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : notifiche.length === 0 ? (
                <div className="text-center py-8 text-muted-color">
                  <Icon name="BellSlashIcon" size={28} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Nessuna notifica</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {notifiche.map(n => {
                    const priorita = n.allarmi?.priorita || 'info';
                    return (
                      <div
                        key={n.id}
                        className={`px-4 py-3 border-l-2 ${PRIORITA_COLORS[priorita] || PRIORITA_COLORS.info} hover:bg-surface-elevated/50 transition-colors`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${PRIORITA_DOT[priorita] || PRIORITA_DOT.info}`} />
                              <p className="text-xs font-semibold text-white truncate">{n.oggetto}</p>
                            </div>
                            <p className="text-xs text-muted-color line-clamp-2">{n.corpo}</p>
                            <p className="text-xs text-muted-color mt-1">{timeAgo(n.created_at)}</p>
                          </div>
                          <button
                            onClick={() => markRead(n.id)}
                            className="text-muted-color hover:text-white transition-colors flex-shrink-0 mt-0.5"
                            title="Segna come letta"
                          >
                            <Icon name="XMarkIcon" size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-4 py-3 border-t border-border">
              <Link
                href="/notifiche"
                onClick={() => setOpen(false)}
                className="block text-center text-xs text-sky-400 hover:text-sky-300 font-semibold transition-colors"
              >
                Vedi tutte le notifiche →
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
